import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faGavel,
  faCoins,
  faUser,
  faRoute,
  faArrowRight,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { useUserData } from '../App';
import ThemeToggle from '../components/ThemeToggle';

const WinningBidPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData } = useUserData();
  
  // 从路由状态获取数据
  const selectedBid = location.state?.selectedBid || null;
  const taskInfo = location.state?.taskInfo || null;
  const adjustableServices = location.state?.adjustableServices || [];
  const customTask = location.state?.customTask || null;

  // 可调整的服务项目
  const [services, setServices] = useState(adjustableServices);

  // 预订信息
  const [bookingInfo, setBookingInfo] = useState({
    name: '',
    phone: '',
    specialRequests: ''
  });

  // 如果没有数据，显示错误信息
  if (!selectedBid || (!taskInfo && !customTask)) {
    return (
      <div className="w-full min-h-screen bg-white dark:bg-mystic-900 font-pixel flex items-center justify-center transition-colors duration-300">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-gray-100 transition-colors duration-300">数据加载失败</h2>
          <p className="mb-4 text-gray-700 dark:text-gray-300 transition-colors duration-300">无法获取中标数据，请返回重新选择方案。</p>
          <button 
            onClick={() => navigate('/task-publish')}
            className="pixel-button px-6 py-3 bg-black dark:bg-mystic-700 text-white border-black dark:border-gray-400 transition-colors duration-300"
          >
            返回发布页面
          </button>
        </div>
      </div>
    );
  }

  // 计算总价
  const getTotalPrice = () => {
    const basePrice = selectedBid.price; // 基础服务费
    const additionalPrice = services
      .filter(service => service.included)
      .reduce((sum, service) => sum + service.price, 0);
    return basePrice + additionalPrice;
  };

  // 切换服务项目
  const toggleService = (serviceId) => {
    setServices(prev => 
      prev.map(service => 
        service.id === serviceId 
          ? { ...service, included: !service.included }
          : service
      )
    );
  };

  // 处理输入变化
  const handleInputChange = (field, value) => {
    setBookingInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 确认支付
  const handleConfirmPayment = () => {
    const totalAmount = getTotalPrice();
    const userBalance = parseFloat(userData.balance.replace(',', ''));
    
    if (userBalance < totalAmount) {
      alert('账户余额不足，请充值后再试！');
      return;
    }

    // 验证必填字段
    if (!bookingInfo.name.trim() || !bookingInfo.phone.trim()) {
      alert('请填写完整的项目联系信息！');
      return;
    }

    if (window.confirm(`确认支付 ¥${totalAmount} 完成此次服务预订吗？`)) {
      // 模拟支付处理
      alert('支付成功！服务已确认，稍后客服将与您联系确认详细安排。');
      navigate('/c-user-center');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-mystic-900 font-pixel transition-colors duration-300">
      {/* Header */}
      <header className="flex justify-between items-center w-full h-20 px-8 border-b-2 border-black dark:border-gray-400 bg-white dark:bg-mystic-800 transition-colors duration-300">
        <div className="flex items-center">
          <div className="pixel-border flex justify-center items-center w-12 h-12 mr-4">
            <FontAwesomeIcon icon={faGavel} className="text-2xl" />
          </div>
          <h1 className="pixel-title text-2xl font-bold uppercase">PolyAgent Market</h1>
        </div>
        <div className="flex items-center">
          {/* Theme Toggle */}
          <ThemeToggle />
          <div className="pixel-border flex items-center h-12 mr-4 ml-4 bg-white dark:bg-mystic-800 transition-colors duration-300">
            <div className="flex justify-center items-center h-full px-4 bg-black dark:bg-mystic-700 text-white transition-colors duration-300">
              <FontAwesomeIcon icon={faCoins} className="mr-2" />
              <div className="text-center">
                <div className="text-xs">{userData.usdtBalance} USDT</div>
                <div className="text-sm font-bold">¥{userData.balance}</div>
              </div>
            </div>
          </div>
          <div className="pixel-border flex items-center h-12 bg-white dark:bg-mystic-800 transition-colors duration-300">
            <div className="flex justify-center items-center h-full px-4 bg-black text-white">
              <span className="text-xl mr-2">{userData.avatar}</span>
              <span className="text-sm font-bold">{userData.username}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full pt-6 px-8 pb-32">
        {/* 中标服务信息 */}
        <div className="pixel-card w-full mb-8 bg-white dark:bg-mystic-800 transition-colors duration-300">
          <div className="flex items-center h-12 px-4 bg-black dark:bg-mystic-700 text-white border-b-2 border-black dark:border-gray-400 transition-colors duration-300">
            <h2 className="pixel-title text-lg font-bold uppercase text-white">中标服务信息</h2>
          </div>
          <div className="flex justify-between items-center p-4">
            <div className="flex items-start">
              <div className="pixel-border flex justify-center items-center w-16 h-16 mr-4 bg-white dark:bg-mystic-700 transition-colors duration-300">
                <FontAwesomeIcon icon={faRoute} className="text-3xl text-amber-600" />
              </div>
              <div>
                <h3 className="mb-2 text-lg font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">
                  {taskInfo ? taskInfo.title : '自定义任务'}
                </h3>
                <p className="mb-1 text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">
                  <span className="font-bold">描述:</span>
                  <span> {taskInfo ? taskInfo.description : customTask.description}</span>
                </p>
                {taskInfo && taskInfo.requirements && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <span className="font-bold">要求:</span>
                    <span> {taskInfo.requirements}</span>
                  </p>
                )}
                {customTask && customTask.ttl && (
                  <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">
                    <span className="font-bold">完成时限:</span>
                    <span> {customTask.ttl}</span>
                  </p>
                )}
              </div>
            </div>
            <div className="border-l-2 border-black dark:border-gray-400 pl-4 transition-colors duration-300">
              <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">
                <span className="font-bold">中标服务商:</span>
                <span> {selectedBid.providerName}</span>
              </p>
              <p className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">
                <span className="font-bold">成功率:</span>
                <span> {selectedBid.successRate}%</span>
              </p>
              <div className="flex items-center mt-2">
                <span className="mr-2 text-sm font-bold text-gray-700 dark:text-gray-300 transition-colors duration-300">状态:</span>
                <span className="bg-black dark:bg-mystic-700 text-white px-2 py-1 text-xs font-bold transition-colors duration-300">已中标</span>
              </div>
            </div>
          </div>
        </div>

        {/* 服务细节 */}
        <div className="pixel-card w-full mb-8 bg-white dark:bg-mystic-800 transition-colors duration-300">
          <div className="flex items-center h-12 px-4 bg-black dark:bg-mystic-700 text-white border-b-2 border-black dark:border-gray-400 transition-colors duration-300">
            <h2 className="pixel-title text-lg font-bold uppercase text-white">服务细节</h2>
          </div>
          <div className="p-4">
            <div className="border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-mystic-700 p-3 mb-4 transition-colors duration-300">
              <ul className="text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">
                {selectedBid.services.map((service, index) => (
                  <li key={index} className="mb-1">• {service}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* 费用明细与调整 */}
        <div className="pixel-card w-full mb-8 bg-white dark:bg-mystic-800 transition-colors duration-300">
          <div className="flex items-center h-12 px-4 bg-black dark:bg-mystic-700 text-white border-b-2 border-black dark:border-gray-400 transition-colors duration-300">
            <h2 className="pixel-title text-lg font-bold uppercase text-white">费用明细与调整</h2>
          </div>
          <div className="p-4">
            <h3 className="mb-3 pl-1 text-sm font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">可调整服务项目</h3>
            {services.map((service) => (
              <div key={service.id} className="flex justify-between items-center mb-3 p-3 border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-mystic-700 transition-colors duration-300">
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <p className="text-sm font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">{service.name}</p>
                    <button 
                      className="pixel-border flex justify-center items-center w-6 h-6 bg-white dark:bg-mystic-600 border-black dark:border-gray-400 transition-colors duration-300"
                      onClick={() => toggleService(service.id)}
                    >
                      {service.included && (
                        <FontAwesomeIcon icon={faCheck} className="text-xs text-green-600" />
                      )}
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 transition-colors duration-300">{service.description}</p>
                </div>
                <div className={`text-right w-20 ml-4 text-sm font-bold transition-colors duration-300 ${
                  service.included ? 'text-black dark:text-white' : 'text-gray-400 dark:text-gray-500 line-through'
                }`}>
                  ¥{service.price.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 项目联系信息 */}
        <div className="pixel-card w-full mb-8 bg-white dark:bg-mystic-800 transition-colors duration-300">
          <div className="flex items-center h-12 px-4 bg-black dark:bg-mystic-700 text-white border-b-2 border-black dark:border-gray-400 transition-colors duration-300">
            <h2 className="pixel-title text-lg font-bold uppercase">项目联系信息</h2>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">联系人姓名</label>
                <input 
                  type="text"
                  className="pixel-input w-full h-10 px-3 bg-white dark:bg-mystic-700 text-gray-900 dark:text-gray-100 border-black dark:border-gray-400 transition-colors duration-300"
                  placeholder="请输入联系人姓名"
                  value={bookingInfo.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                />
              </div>
              <div>
                <label className="block mb-2 text-sm font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">联系电话</label>
                <input 
                  type="text"
                  className="pixel-input w-full h-10 px-3 bg-white dark:bg-mystic-700 text-gray-900 dark:text-gray-100 border-black dark:border-gray-400 transition-colors duration-300"
                  placeholder="请输入联系电话"
                  value={bookingInfo.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </div>
              <div className="col-span-2">
                <label className="block mb-2 text-sm font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">特殊需求</label>
                <textarea 
                  className="pixel-input w-full p-3 h-20 resize-none bg-white dark:bg-mystic-700 text-gray-900 dark:text-gray-100 border-black dark:border-gray-400 transition-colors duration-300"
                  placeholder="如有特殊需求或项目备注请在此说明"
                  value={bookingInfo.specialRequests}
                  onChange={(e) => handleInputChange('specialRequests', e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* 固定底部支付栏 */}
      <div className="fixed-bottom p-8 z-50 bg-white dark:bg-mystic-800 border-t-2 border-black dark:border-gray-400 transition-colors duration-300">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <div className="mr-6">
              <p className="mb-1 text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">服务总价</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 transition-colors duration-300">¥{getTotalPrice().toLocaleString()}</p>
            </div>
            <div className="border-l-2 border-black dark:border-gray-400 pl-6 transition-colors duration-300">
              <p className="mb-1 text-sm text-gray-700 dark:text-gray-300 transition-colors duration-300">账户余额</p>
              <div className="text-right">
                <p className="text-sm font-bold text-gray-700 dark:text-gray-300 transition-colors duration-300">{userData.usdtBalance} USDT</p>
                <p className="text-lg font-bold text-amber-600 dark:text-amber-400 transition-colors duration-300">¥{userData.balance}</p>
              </div>
            </div>
          </div>
          <button 
            className="pixel-button flex items-center h-15 px-8 text-lg font-bold uppercase bg-black dark:bg-mystic-700 text-white border-black dark:border-gray-400 transition-colors duration-300"
            onClick={handleConfirmPayment}
          >
            <span>确认支付</span>
            <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WinningBidPage;